import React, { useState, useEffect } from 'react';
import './balances.css';
import { PoolEntryActions } from './PoolEntryActions.jsx';
import { ACTIONABLE_POOL_TYPES } from '../lib/poolEntries.js';
import { useInvestmentData } from '../lib/useInvestmentData.js';
import { getSupabase } from '../lib/supabase.js';
import CostLinesCard from '../components/CostLinesCard.jsx';
import { movableNow, HOLD_HOURS } from '../lib/holdCalc.js';

// Balances tab — "how much is in each app" (Coinbase, SoFi, …), the #3
// daily investor need. Live balance per account is derived in
// useInvestmentData: balance_anchor + net of movements logged after the
// anchor. Accounts start as pure auto-tally (anchor 0); tapping Edit
// re-anchors to the real number and badges the account "edited".
//
// Reachable via the mobile bottom nav AND, since T-008, the desktop top nav
// (DesktopNav) — plus the mobile Home "Manage →" link. Desktop renders the
// same phone-first layout inside the shared centered .page container.

const fmt$ = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.abs(Math.round(n));
  return (n < 0 ? '-$' : '$') + v.toLocaleString('en-US');
};

// Deterministic tile color per institution string.
const TILE = ['#4f46e5', '#0ea5e9', '#059669', '#d97706', '#db2777', '#7c3aed', '#dc2626', '#0d9488'];
function tileColor(s) {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TILE[h % TILE.length];
}
const tileInit = (s) => (s || '?').trim().slice(0, 2).toUpperCase();

// Account icon: the institution's brand logo (inv_account_kinds.logo_url via
// data.balances[].kindLogo) when available, falling back to a colored initials
// tile. Logos make the list far easier to scan than abbreviations.
function AcctIcon({ row }) {
  const [failed, setFailed] = useState(false);
  if (row.kindLogo && !failed) {
    return (
      <div className="bal-ico bal-ico-logo">
        <img src={row.kindLogo} alt="" loading="lazy" onError={() => setFailed(true)} />
      </div>
    );
  }
  return <div className="bal-ico" style={{ background: tileColor(row.institution) }}>{tileInit(row.institution)}</div>;
}

// "Mon, Sep 28 · 3:10 PM" in the viewer's own timezone — the timer is about
// their wall clock, not the ledger's UTC day.
const whenLabel = (ms) => {
  try {
    return new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};
// "in 5h 12m" / "in 2 days" — how long until a timer ends.
function untilLabel(ms, now) {
  const d = Math.max(0, ms - now);
  const h = Math.floor(d / 3600000);
  const m = Math.floor((d % 3600000) / 60000);
  if (h >= 48) return `in ${Math.round(h / 24)} days`;
  if (h >= 1) return `in ${h}h ${m}m`;
  return m <= 1 ? 'in a minute' : `in ${m}m`;
}

// T-052 · the 48-hour hold box. Every deposit starts its own 48h timer; the
// box shows balance minus the deposits still on a timer. Informational only:
// nothing reads it to allow or block a movement, nothing is stored, and any
// admin correction to the balance or a movement flows straight through.
function HoldBox({ row, movements, now, open, onToggle }) {
  const { movable, heldIn, holds, nextReleaseAt } = movableNow(row, movements, now);
  if (movable === null) return null;
  const clear = holds.length === 0;
  return (
    <button
      type="button"
      className={'bal-hold' + (clear ? ' clear' : ' held') + (open ? ' open' : '')}
      onClick={onToggle}
      aria-expanded={open}
      title={clear ? `No deposits inside the ${HOLD_HOURS}-hour hold` : `${fmt$(heldIn)} landed in the last ${HOLD_HOURS} hours`}
    >
      <span className="bal-hold-l">{clear ? 'All movable' : 'Movable now'}</span>
      <span className="bal-hold-v">{fmt$(movable)}</span>
      {!clear && nextReleaseAt && (
        <span className="bal-hold-next">+{fmt$(holds[0].amount)} {untilLabel(nextReleaseAt, now)}</span>
      )}
    </button>
  );
}

// The breakdown under a row when its hold box is open.
function HoldDetail({ row, movements, now }) {
  const { movable, heldIn, holds } = movableNow(row, movements, now);
  return (
    <div className="bal-hold-detail">
      <div className="bal-hold-detail-head">
        <span>{HOLD_HOURS}-hour hold · estimate only</span>
        <span>{fmt$(row.balance)} balance − {fmt$(heldIn)} on hold = <strong>{fmt$(movable)}</strong></span>
      </div>
      {holds.length === 0 ? (
        <div className="bal-hold-line muted">Nothing landed here in the last {HOLD_HOURS} hours, so the whole balance has aged past the hold.</div>
      ) : holds.map((h) => (
        <div key={h.id} className="bal-hold-line">
          <span className="bhl-amt">{fmt$(h.amount)}</span>
          <span className="bhl-when">landed {whenLabel(h.landedAt)}</span>
          <span className="bhl-free">frees {untilLabel(h.releasesAt, now)} · {whenLabel(h.releasesAt)}</span>
        </div>
      ))}
      <div className="bal-hold-foot">
        Every deposit you log starts its own {HOLD_HOURS}-hour timer; what you can move is the balance minus
        deposits still on a timer. A guide, not a limit — the app records any transfer you log, and
        editing a balance or a movement updates this figure automatically.
      </div>
    </div>
  );
}

export default function Balances({ member, profile, actingAsAdmin = false }) {
  const { status, data, reload } = useInvestmentData(member, profile);
  const [editing, setEditing] = useState(null); // balance row being edited
  // T-052: which row's hold breakdown is open, and a minute clock so a box
  // jumps the moment a 48h timer ends instead of waiting for a reload.
  const [holdOpen, setHoldOpen] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Tk-C pool position (T-009 / D22 §2): household-level, from the
  // v2_pool_position RPC. available = confirmed sends to Kareem Trust/Kraken
  // minus confirmed TK-C draws; pending becomes available when Kareem
  // confirms the movement (owner decision 2026-08-04). The RPC returns one
  // row for an investor and all households for an admin (act-as included),
  // so pick this member's household client-side. null = hide the card
  // (e.g. the RPC isn't deployed yet, or the profile isn't linked).
  const [pool, setPool] = useState(null);
  const [poolActivity, setPoolActivity] = useState(null); // canonical per-line pool history
  const [poolReload, setPoolReload] = useState(0);
  // An Edit / Delete on a pool entry (here, Home or Activity) re-reads the pool.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const bump = () => setPoolReload((k) => k + 1);
    window.addEventListener('finops:enrollment-changed', bump);
    return () => window.removeEventListener('finops:enrollment-changed', bump);
  }, []);
  const canEditPool = profile?.role === 'admin';
  useEffect(() => {
    let cancelled = false;
    const sb = getSupabase();
    if (!sb || !member) return undefined;
    (async () => {
      const hh = member.managed_by_member_id || member.id;
      const [{ data: rows, error }, act] = await Promise.all([
        sb.rpc('v2_pool_position'),
        sb.rpc('v2_pool_activity', { p_limit: 200 }).then((r) => r, () => ({ data: null })),
      ]);
      if (cancelled || error || !Array.isArray(rows)) return;
      const row = rows.find((r) => r.household === hh);
      setPool(row || { household: hh, available: 0, pending_in: 0, pending_out: 0 });
      if (Array.isArray(act?.data)) {
        setPoolActivity(act.data.filter((e) => e.household === hh));
      }
    })();
    return () => { cancelled = true; };
  }, [member?.id, poolReload]);

  if (status === 'loading') {
    return <div className="page"><div className="bal-empty">Loading balances…</div></div>;
  }

  const balances = (data && data.balances) || [];
  const balanceMovements = (data && data.balanceMovements) || [];
  const total = (data && data.balanceTotal) || 0;
  const setCount = (data && data.balancesSetCount) || 0;
  const members = (data && data.members) || [];

  // Per-member rollup (in member order: holder first). We keep members with
  // zero accounts so every sub-account stays visible — they render an empty
  // "no accounts yet" hint instead of silently disappearing from the household.
  const byMember = members
    .map((m) => {
      const rows = balances.filter((b) => b.memberId === m.id);
      return { member: m, rows, total: rows.reduce((s, b) => s + (b.isSet ? b.balance : 0), 0) };
    });

  return (
    <div className="page bal-page">
      <div className="page-title-row">
        <div>
          <div className="page-eyebrow">Where your money is</div>
          <h1 className="page-title">Balances</h1>
        </div>
      </div>

      <div className="bal-note">
        Set each account's real balance once — then the movements you log keep it current
        automatically. Tap <strong>Set balance</strong> on any account to start.
      </div>

      {pool && (
        <div className="bal-pool-card">
          <div className="bal-pool-head">
            <div>
              <div className="bal-pool-eye">Tk-C pool · household</div>
              {/* T-015: sign-colored — green in the black, rose in the red. */}
              <div className={'bal-pool-amt' + (Number(pool.available) > 0 ? ' pos' : Number(pool.available) < 0 ? ' neg' : '')}>{fmt$(pool.available)}</div>
            </div>
            <div className="bal-pool-chips">
              {Number(pool.pending_in) > 0 && (
                <span className="bal-pool-chip in">+{fmt$(pool.pending_in)} pending</span>
              )}
              {Number(pool.pending_out) > 0 && (
                <span className="bal-pool-chip out">−{fmt$(pool.pending_out)} draws pending</span>
              )}
            </div>
          </div>
          {Number(pool.from_gold) > 0 && (
            <div className="bal-pool-gold">includes {fmt$(pool.from_gold)} from gold payouts</div>
          )}
          {/* D-9: investment payouts converted to pool credit (the invest
              bridge) — same treatment as the gold slice. */}
          {Number(pool.from_investment) > 0 && (
            <div className="bal-pool-gold">includes {fmt$(pool.from_investment)} from investment payouts</div>
          )}
          {Number(pool.carryover) !== 0 && !Number.isNaN(Number(pool.carryover)) && (
            <div className="bal-pool-gold">ledger carryover: {fmt$(pool.carryover)}</div>
          )}
          <div className="bal-pool-sub">
            Funded by your transfers to Kareem Trust / Kareem Kraken, by gold payouts you've pooled,
            and by investment credits Kareem converts to pool balance; drawn down by your household's
            TK-C card use. Pending amounts become available once Kareem confirms the transfer.
          </div>

          {/* Canonical per-line pool history (T-009): every contribution,
              TK-C draw, gold bridge, and ledger-carryover entry. Draws are
              stored positive but shown negative — they leave the pool. */}
          {poolActivity && poolActivity.length > 0 && (
            <div className="bal-pool-act">
              <div className="bal-pool-act-head">Pool activity</div>
              {poolActivity.slice(0, 20).map((e) => {
                const shown = e.entry_type === 'draw' ? -Number(e.amount) : Number(e.amount);
                const typeLabel = { contribution: 'Contribution', draw: 'TK-C draw', gold_bridge: 'Gold payout', invest_bridge: 'Investment payout', carryover: 'Carryover' }[e.entry_type] || e.entry_type;
                return (
                  <div key={e.movement_id} className="bal-pool-act-row">
                    <span className="bpa-date">{(e.entry_date || '').slice(0, 10)}</span>
                    <span className={'bpa-type bpa-' + e.entry_type}>{typeLabel}</span>
                    {e.status === 'pending' && <span className="bpa-pending">pending</span>}
                    <span className="bpa-note">{e.note || ''}</span>
                    <span className={'bpa-amt' + (shown < 0 ? ' neg' : '')}>{shown < 0 ? '−' : '+'}{fmt$(Math.abs(shown)).replace('$', '$')}</span>
                    {/* Everything but TK-C draws (Taekus-fetched; the review
                        card handles those). A pending pool payment gets
                        Reject — a duplicate must be removable where it shows. */}
                    {canEditPool && ACTIONABLE_POOL_TYPES.includes(e.entry_type) && (
                      <PoolEntryActions compact entry={{ movementId: e.movement_id, poolType: e.entry_type, status: e.status }} onDone={reload} />
                    )}
                  </div>
                );
              })}
              {poolActivity.length > 20 && (
                <div className="bal-pool-act-more">Showing the latest 20 of {poolActivity.length} entries.</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bal-total-card">
        <span className="btc-l">Total across accounts<br /><span className="btc-sub">{setCount} of {balances.length} set</span></span>
        <span className="btc-v">{fmt$(total)}</span>
      </div>

      {/* T-051: the household's costs, line by line — investors edit what they
          entered (booked lines go to Kareem for confirmation); an admin acting
          as the investor edits directly. */}
      <CostLinesCard member={member} members={members} actingAsAdmin={actingAsAdmin} />

      {balances.length === 0 ? (
        <div className="bal-empty">
          No accounts yet. They're created automatically the first time you log a movement —
          then their balances show up here.
        </div>
      ) : (
        byMember.map((g) => (
          <section key={g.member.id} className="bal-group">
            <div className="bal-group-head">
              <span className="avatar" style={{ width: 22, height: 22, fontSize: 10, background: g.member.color }}>{g.member.initials}</span>
              <span className="bgh-name">{g.member.role === 'holder' ? `${g.member.name} (you)` : g.member.name}</span>
              <span className="bgh-total">{fmt$(g.total)}</span>
            </div>
            <div className="bal-list">
              {g.rows.length === 0 && (
                <div className="bal-row bal-row-empty">
                  <div className="bal-info">
                    <div className="bal-sub">No accounts yet — added automatically when a movement is logged.</div>
                  </div>
                </div>
              )}
              {g.rows.map((b) => (
                <React.Fragment key={b.accountId}>
                <div className={'bal-row' + (holdOpen === b.accountId ? ' bal-row-open' : '')}>
                  <AcctIcon row={b} />
                  <div className="bal-info">
                    <div className="bal-inst">{b.institution}</div>
                    <div className="bal-sub">
                      {b.kindLabel}
                      {b.isSet
                        ? <span className="bal-badge edited">set{b.updatedAtLabel ? ` ${b.updatedAtLabel}` : ''}</span>
                        : <span className="bal-badge auto">not set</span>}
                    </div>
                  </div>
                  <div className="bal-right">
                    {b.isSet ? (
                      <>
                        <div className="bal-amt">{fmt$(b.balance)}</div>
                        {/* Pending movements are held OUT of the balance —
                            confirming is the only trigger — but shown here so
                            in-flight money is visible rather than missing. */}
                        {b.pendingDelta ? (
                          <div className="bal-pending">
                            {b.pendingDelta > 0 ? '+' : '−'}{fmt$(Math.abs(b.pendingDelta))} awaiting confirmation
                          </div>
                        ) : null}
                        <HoldBox
                          row={b}
                          movements={balanceMovements}
                          now={now}
                          open={holdOpen === b.accountId}
                          onToggle={() => setHoldOpen(holdOpen === b.accountId ? null : b.accountId)}
                        />
                        <button type="button" className="bal-edit" onClick={() => setEditing(b)}>Edit</button>
                      </>
                    ) : (
                      <>
                        {b.pendingDelta ? (
                          <div className="bal-pending">
                            {b.pendingDelta > 0 ? '+' : '−'}{fmt$(Math.abs(b.pendingDelta))} awaiting confirmation
                          </div>
                        ) : null}
                        <button type="button" className="bal-setbtn" onClick={() => setEditing(b)}>Set balance</button>
                      </>
                    )}
                  </div>
                </div>
                {holdOpen === b.accountId && b.isSet && (
                  <HoldDetail row={b} movements={balanceMovements} now={now} />
                )}
                </React.Fragment>
              ))}
            </div>
          </section>
        ))
      )}

      {editing && (
        <EditBalanceModal
          row={editing}
          member={member}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); if (typeof reload === 'function') reload(); }}
        />
      )}
    </div>
  );
}

// Correct an account's balance to the real number. Writes the anchor +
// timestamp + manual flag; the live balance recomputes from there.
function EditBalanceModal({ row, member, onClose, onSaved }) {
  const [value, setValue] = useState(row.isSet ? String(row.balance ?? 0) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    const amt = Number(value);
    if (!isFinite(amt)) { setErr('Enter a valid number.'); return; }
    setSaving(true); setErr(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      const { error } = await sb.from('financial_accounts')
        .update({
          balance_anchor: amt,
          balance_anchor_at: new Date().toISOString(),
          balance_set_manually: true,
        })
        .eq('id', row.accountId);
      if (error) throw error;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
      }
      onSaved();
    } catch (e) {
      setErr(e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div className="bal-modal-bd" onClick={onClose}>
      <div className="bal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bal-modal-head">
          <AcctIcon row={row} />
          <div>
            <div className="bal-modal-eye">Correct balance</div>
            <h2 className="bal-modal-title">{row.institution}</h2>
            <div className="bal-modal-sub">{row.kindLabel} · {row.memberName}</div>
          </div>
          <button type="button" className="bal-modal-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {err && <div className="bal-modal-err">{err}</div>}

        <label className="bal-modal-lbl">Actual balance right now ($)</label>
        <input
          className="bal-modal-input"
          type="number"
          inputMode="decimal"
          step="0.01"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onWheel={(e) => e.currentTarget.blur()}
        />
        <p className="bal-modal-hint">
          Future movements you log will adjust this automatically.
        </p>

        <div className="bal-modal-actions">
          <button type="button" className="bal-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="bal-btn-pri" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save balance'}
          </button>
        </div>
      </div>
    </div>
  );
}
